import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../utils/api';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import { useCommunityLang } from '../context/CommunityLangContext';
import CommunityLangToggle from '../components/community/CommunityLangToggle';
import { Users, Mail, Lock, User, ArrowRight, LogIn, UserPlus } from 'lucide-react';

const TEAL = '#4DB6AC';

export default function CommunityLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useCommunityAuth();
  const { t } = useCommunityLang();
  const [isRegister, setIsRegister] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If the user was redirected here from a protected page (e.g. a shared
  // /community/posts/:id link), send them back there after login. Falls
  // back to /community.
  const redirectTo = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : '/community';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (isRegister && form.username.includes('@')) { setError("Username can't contain '@'. Choose a nickname. Please don't use your email or full name."); return; }
    if (isRegister && form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      const endpoint = isRegister ? '/community/auth/register' : '/community/auth/login';
      const body = isRegister ? form : { email: form.email, password: form.password };
      const res = await axios.post(`${API}${endpoint}`, body);
      login(res.data.access_token, res.data.user);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || t('common.something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAF9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <CommunityLangToggle />
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 64, height: 64, borderRadius: 20, background: `linear-gradient(135deg, ${TEAL}, #2E8B7A)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Users size={32} color="#fff" />
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1F2937', marginBottom: 4 }}>{t('login.app_name')}</h1>
          <p style={{ fontSize: 14, color: '#6B7280' }}>{t('login.tagline')}</p>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', marginBottom: 24, background: '#F3F4F6', borderRadius: 10, padding: 3 }}>
            {[{ label: t('login.sign_in'), testid: 'sign-in', icon: LogIn, val: false }, { label: t('login.register'), testid: 'register', icon: UserPlus, val: true }].map(tab => (
              <button key={tab.testid} type="button" onClick={() => { setIsRegister(tab.val); setError(''); }}
                data-testid={`tab-${tab.testid}`}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  background: isRegister === tab.val ? '#fff' : 'transparent',
                  color: isRegister === tab.val ? TEAL : '#9CA3AF',
                  boxShadow: isRegister === tab.val ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.2s' }}>
                <tab.icon size={16} /> {tab.label}
              </button>
            ))}
          </div>

          {error && (
            <div data-testid="login-error" style={{ padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA',
              borderRadius: 8, fontSize: 13, color: '#B91C1C', marginBottom: 16 }}>{error}</div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {isRegister && (
              <div>
              <div style={{ position: 'relative' }}>
                <User size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input data-testid="input-username" placeholder={t('login.username_placeholder')} value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })} required
                  style={{ width: '100%', boxSizing: 'border-box', padding: '12px 12px 12px 40px', borderRadius: 10,
                    border: '1.5px solid #E5E7EB', fontSize: 15, outline: 'none' }}
                  onFocus={(e) => e.target.style.borderColor = TEAL}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
              </div>
              <p data-testid="username-hint" style={{ fontSize: 11, color: '#6B7280', margin: '6px 2px 0' }}>Choose a nickname. Please don't use your email or full name.</p>
              </div>
            )}
            <div style={{ position: 'relative' }}>
              <Mail size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
              <input data-testid="input-email" type="email" placeholder={t('login.email_placeholder')} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} required
                style={{ width: '100%', boxSizing: 'border-box', padding: '12px 12px 12px 40px', borderRadius: 10,
                  border: '1.5px solid #E5E7EB', fontSize: 15, outline: 'none' }}
                onFocus={(e) => e.target.style.borderColor = TEAL}
                onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
            </div>
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
              <input data-testid="input-password" type="password" placeholder={t('login.password_placeholder')} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} required
                style={{ width: '100%', boxSizing: 'border-box', padding: '12px 12px 12px 40px', borderRadius: 10,
                  border: '1.5px solid #E5E7EB', fontSize: 15, outline: 'none' }}
                onFocus={(e) => e.target.style.borderColor = TEAL}
                onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
            </div>
            <button type="submit" data-testid="submit-btn" disabled={loading}
              style={{ width: '100%', padding: '14px', background: loading ? '#9CA3AF' : TEAL, color: '#fff',
                border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: '0 4px 12px rgba(77,182,172,0.25)', transition: 'all 0.2s' }}>
              {loading ? t('common.please_wait') : (<>{isRegister ? t('login.create_account') : t('login.sign_in_btn')} <ArrowRight size={18} /></>)}
            </button>

            {!isRegister && (
              <button type="button" data-testid="forgot-password-link"
                onClick={() => navigate('/community/forgot-password')}
                style={{ display: 'block', margin: '-4px auto 0', background: 'none', border: 'none',
                  color: TEAL, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                {t('login.forgot_password')}
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
